// Delivery / Collection Note draft: the pure logic behind the WYSIWYG note editor, shared by the warehouse (Inbound -> Notes) and the
// Job -> Transport tab, so the note's fields, the rows written to delivery_notes and the data handed to the PDF are decided in ONE place.
// (Before Z44 the warehouse page had this inline and Transport had its own HaulageForm/rowFromForm: two copies of the same format.)
// Pure on purpose (no React, no Supabase): see deliveryNoteDraft.test.ts. The IO (insert/update + PDF upload) is in deliveryNoteSave.ts.
// Run: node --test --experimental-strip-types src/lib/deliveryNoteDraft.test.ts
import type { DeliveryNote, DeliveryNoteType } from '../pages/Warehouse/types.ts'
import { fmtDate } from '../pages/Warehouse/types.ts'
import type { PickedAddress } from '../pages/Warehouse/Devannings/AddressPicker.tsx'
import type { CompanySettings } from './companySettings.ts'
import type { DeliveryCollectionNoteData, DCNGoodsRow, DCNAddressBox } from '../pages/Warehouse/Devannings/DeliveryCollectionNotePDF.tsx'
import { suggestFulfilmentForNote, noteKindForMethodCode, commonMethodId, type FulfilmentType, type DeliveryMethodRow } from './fulfilmentType.ts'
import { deliveryPostcodeForSave } from './ukPostcode.ts'

export interface ItemGroupLite {
  key: string
  clientRefSuffix: string | null
  cartons: number
  deliveryMethods: (string | null)[]
  deliveryMethodIds: (string | null)[]
}

// The subset of fields with a real "auto-computed" default a user can override and later
// reset -- everything else (Collect From, Release To, dates, Haulier, free-text Notes...)
// is just plain editable with no distinct "auto" value, so it isn't tracked here.
export type AutoFieldKey = 'nop' | 'weightKg' | 'cbm' | 'remarks' | 'authoriseTo' | 'description' | 'summary'
export interface NoteDraft {
  noteType: DeliveryNoteType
  reissueOf: string | null
  // The Inbound row (devanning group key) this note was raised from -- its quantities come in
  // automatically; NOP can be lowered for a partial shipment.
  sourceKey: string | null
  customerRef: string
  collectFrom: PickedAddress
  // Who takes it from us -- a courier/customer/supplier/CY UK's own driver, always filled in
  // regardless of fulfilmentType (Gary, 2026-09-28: "先release给haulier(谁来取)").
  releaseTo: PickedAddress
  vehicleReg: string  // COLLECTION_SELF / COLLECTION_SUPPLIER only
  driverName: string  // COLLECTION_SELF / COLLECTION_SUPPLIER only
  scheduledDate: string
  scheduledTime: string
  marks: string
  // null = following the auto value derived from the selected items (distinct descriptions /
  // pallet count); a string (even '') = staff typed it.
  descriptionOverride: string | null
  summaryOverride: string | null
  notes: string
  // Present key = user has overridden that field away from its auto-computed value;
  // the override's own current value lives in the field-specific state below.
  overrides: Partial<Record<AutoFieldKey, string>>
  nopOverride: number | null
  weightKgOverride: number | null
  cbmOverride: number | null
  remarksOverride: string | null
  authoriseToOverride: string | null
  // non-null means staff has explicitly picked a value (same override-field convention as
  // nopOverride etc. above: null = still following the live suggestion from
  // lib/fulfilmentType.ts, set = overridden, "reset" sets it back to null).
  fulfilmentType: FulfilmentType | null
  // pricing_delivery_methods.id staff picked at note level; null = follow the selected items'
  // common method. Decides the note kind (title/labels) AND the fulfilment default.
  deliveryMethodId: string | null
  // The final consignee -- only shown/used when fulfilmentType is COLLECTION_AND_DELIVERY. No
  // auto-default (unlike collectFrom); always a fresh pick per shipment.
  deliverTo: PickedAddress
}

export function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const emptyAddress = (): PickedAddress => ({ name: '', lines: [], phone: null, customerId: null, warehouseId: null })

export function blankNoteDraft(noteType: DeliveryNoteType, sourceKey: string | null, notes: string, autoAddr: PickedAddress | null = null): NoteDraft {
  return {
    noteType, reissueOf: null, sourceKey, customerRef: '',
    // Collect From starts pre-filled with the devanning's own warehouse on the warehouse side (autoAddr); Release To and Deliver To start empty.
    collectFrom: autoAddr || emptyAddress(),
    releaseTo: emptyAddress(),
    deliverTo: emptyAddress(),
    vehicleReg: '', driverName: '',
    scheduledDate: todayISO(), scheduledTime: '',
    marks: '', descriptionOverride: null, summaryOverride: null, notes,
    overrides: {}, nopOverride: null, weightKgOverride: null, cbmOverride: null,
    remarksOverride: null, authoriseToOverride: null,
    fulfilmentType: null,
    deliveryMethodId: null,
  }
}

// overridden_fields (JSONB flags saved alongside the note) decide which stored value is a manual override vs. still just the auto-computed
// figure -- comparing the stored value against a freshly recomputed auto value isn't reliable (they could coincidentally match).
export function noteDraftFromNote(note: DeliveryNote, sourceKey: string | null): NoteDraft {
  const overridden = note.overridden_fields || {}
  return {
    noteType: note.note_type, reissueOf: note.id,
    sourceKey,
    customerRef: note.customer_ref || '',
    collectFrom: {
      name: note.collect_from_code || '', lines: (note.collect_from_addr || '').split('\n').filter(Boolean),
      phone: null, customerId: note.collect_from_customer_id, warehouseId: note.collect_from_warehouse_id,
    },
    releaseTo: {
      name: note.release_to_name || '', lines: (note.release_to_addr || '').split('\n').filter(Boolean),
      phone: note.release_to_phone, customerId: note.release_to_customer_id, warehouseId: note.release_to_warehouse_id,
    },
    deliverTo: {
      name: note.deliver_to_name || '', lines: (note.deliver_to_addr || '').split('\n').filter(Boolean),
      phone: note.deliver_to_phone, customerId: note.deliver_to_customer_id, warehouseId: null,
    },
    vehicleReg: note.vehicle_reg || '', driverName: note.driver_name || '',
    scheduledDate: note.scheduled_date, scheduledTime: note.scheduled_time || '',
    marks: (note.marks_numbers || []).join('\n'),
    descriptionOverride: overridden.description ? (note.description || '') : null,
    summaryOverride: overridden.summary ? (note.summary_text || '') : null,
    notes: note.notes || '',
    overrides: {},
    nopOverride: overridden.nop ? note.nop : null,
    weightKgOverride: overridden.weightKg ? note.weight_kg : null,
    cbmOverride: overridden.cbm ? note.cube_cbm : null,
    remarksOverride: overridden.remarks ? note.remarks : null,
    authoriseToOverride: overridden.authoriseTo ? note.authorise_to : null,
    // overridden flag decides whether the stored value is a manual pick or just whatever was auto-suggested at save time.
    fulfilmentType: note.fulfilment_type_overridden ? note.fulfilment_type : null,
    // null on notes saved before the column existed -- the caller fills it in from note_type once the methods list is loaded.
    deliveryMethodId: note.delivery_method_id ?? null,
  }
}

export function addressBox(label: string, addr: PickedAddress, extra?: { inlineLabel?: string; inlineValue?: string | null }): DCNAddressBox {
  return { label, lines: addr.name ? [addr.name, ...addr.lines] : [], phone: addr.phone, ...extra }
}

export function buildGoods(marks: string, description: string, summary: string, cartons: number, weightKg: number | null, cbm: number | null): DCNGoodsRow[] {
  const lines = marks.split('\n').map(s => s.trim()).filter(Boolean)
  if (lines.length === 0) return [{ marksNumbers: '', description, summary, nop: cartons, weight: weightKg, cube: cbm }]
  return lines.map((l, i) => ({
    marksNumbers: l, description: i === 0 ? description : null, summary: i === 0 ? summary : null,
    nop: i === 0 ? cartons : null, weight: i === 0 ? weightKg : null, cube: i === 0 ? cbm : null,
  }))
}

// The auto-computed default of every overridable field. The warehouse derives them from the devanning rows; a note raised on the Transport
// tab has no devanning, so its "auto" values are empty (standaloneAuto) and everything on it is typed in.
export interface NoteAuto {
  nop: number
  weightKg: number | null
  cbm: number | null
  remarks: string
  authoriseTo: string
  description: string
  summary: string
  pallets: number
  itemIds: string[]
}

export function standaloneAuto(d: NoteDraft): NoteAuto {
  // Pallets are typed in the Summary box as text ("15 PLTS"); the numeric column follows it.
  const m = (d.summaryOverride ?? '').match(/(\d+)\s*PLT/i)
  return { nop: 0, weightKg: null, cbm: null, remarks: '', authoriseTo: d.releaseTo.name, description: '', summary: '', pallets: m ? parseInt(m[1], 10) : 0, itemIds: [] }
}

export interface ResolvedNote {
  methodId: string | null
  noteType: DeliveryNoteType
  fulfilment: { type: FulfilmentType | null; overridden: boolean }
}

// The delivery method, the note kind and the fulfilment type of a draft. `groups` are the devanning rows the note is raised from
// (the warehouse side); an empty list means "no devanning" and only what the draft itself carries counts.
export function resolveNote(d: NoteDraft, methods: DeliveryMethodRow[], groups: ItemGroupLite[]): ResolvedNote {
  const sel = groups.filter(g => g.key === d.sourceKey)
  const methodId = d.deliveryMethodId ?? commonMethodId(sel.flatMap(g => g.deliveryMethodIds))
  const m = methods.find(x => x.id === methodId)
  const noteType = m ? noteKindForMethodCode(m.code) : d.noteType
  const inputs = sel.flatMap(g => g.deliveryMethods.map((text, i) => ({ deliveryMethodId: g.deliveryMethodIds[i], deliveryMethodText: text })))
  const auto = suggestFulfilmentForNote(methodId, methods, inputs)
  return { methodId, noteType, fulfilment: { type: d.fulfilmentType ?? auto.type, overridden: d.fulfilmentType !== null } }
}

// The data the PDF is drawn from. `issuedAt` is a parameter (frozen once by the caller at the moment of generating and written to the DB row
// as well), so the PDF and the stored issued_at can never drift apart.
export function buildNoteData(a: {
  draft: NoteDraft; auto: NoteAuto; resolved: ResolvedNote; jobRef: string; company: CompanySettings
  serialNo: number | null; issuerName: string; issuedAt: string; readinessStatus?: DeliveryNote['readiness_status']
}): DeliveryCollectionNoteData {
  const { draft: d, auto, resolved, jobRef } = a
  const nop = d.nopOverride ?? auto.nop
  const weightKg = d.weightKgOverride ?? auto.weightKg
  const cbm = d.cbmOverride ?? auto.cbm
  const remarks = d.remarksOverride ?? auto.remarks
  const authoriseTo = d.authoriseToOverride ?? auto.authoriseTo
  const description = d.descriptionOverride ?? auto.description
  const summary = d.summaryOverride ?? auto.summary
  const releaseToBox = addressBox('Release To:', d.releaseTo)
  if (resolved.noteType !== 'COLLECTION_COURIER') {
    if (d.vehicleReg) releaseToBox.lines.push(`Vehicle: ${d.vehicleReg}`)
    if (d.driverName) releaseToBox.lines.push(`Driver: ${d.driverName}`)
  }
  return {
    company: a.company,
    noteType: resolved.noteType,
    customerRef: d.customerRef || (jobRef && d.releaseTo.name ? `${jobRef}-${d.releaseTo.name}` : jobRef || null),
    jobRef,
    collectFrom: addressBox('Collect From:', d.collectFrom, { inlineLabel: 'Collection Details', inlineValue: d.scheduledDate ? fmtDate(d.scheduledDate) : null }),
    releaseTo: releaseToBox,
    deliverTo: resolved.fulfilment.type === 'COLLECTION_AND_DELIVERY' ? addressBox('Deliver To:', d.deliverTo) : null,
    goods: buildGoods(d.marks, description, summary, nop, weightKg, cbm),
    remarks,
    notes: d.notes,
    authoriseTo,
    serialNo: a.serialNo,
    issuedByName: a.issuerName,
    issuedAt: a.issuedAt,
    readinessStatus: a.readinessStatus ?? null,
  }
}

// The delivery_notes row for a draft -- shared by autosave, Save Draft, Generate PDF and Push so they can never diverge.
// `devanningId` is null for a note raised on the Transport tab; the caller adds what only it knows (note_source, job_id, lifecycle_status).
export function buildNoteRow(a: {
  draft: NoteDraft; auto: NoteAuto; resolved: ResolvedNote; jobRef: string; devanningId: string | null
  issuer: { userId: string | null; name: string }; previousNote: DeliveryNote | null
  // The job this note belongs to, when the caller knows it (the warehouse page looks it up by job ref). Without it the Job -> Transport tab
  // can only find the note by matching job_ref text, and warehouse refs and job refs do not always match.
  jobId?: string | null
}) {
  const { draft: d, auto, resolved, jobRef } = a
  const isCollectionAndDelivery = resolved.fulfilment.type === 'COLLECTION_AND_DELIVERY'
  const nop = d.nopOverride ?? auto.nop
  const weightKg = d.weightKgOverride ?? auto.weightKg
  const cbm = d.cbmOverride ?? auto.cbm
  const remarks = d.remarksOverride ?? auto.remarks
  const authoriseTo = d.authoriseToOverride ?? auto.authoriseTo
  const description = d.descriptionOverride ?? auto.description
  const summary = d.summaryOverride ?? auto.summary
  const noteType = resolved.noteType
  const overriddenFields: Partial<Record<AutoFieldKey, boolean>> = {}
  if (d.nopOverride !== null) overriddenFields.nop = true
  if (d.weightKgOverride !== null) overriddenFields.weightKg = true
  if (d.cbmOverride !== null) overriddenFields.cbm = true
  if (d.remarksOverride !== null) overriddenFields.remarks = true
  if (d.authoriseToOverride !== null) overriddenFields.authoriseTo = true
  if (d.descriptionOverride !== null) overriddenFields.description = true
  if (d.summaryOverride !== null) overriddenFields.summary = true
  return {
    note_type: noteType,
    delivery_method_id: resolved.methodId,
    devanning_id: a.devanningId,
    ...(a.jobId ? { job_id: a.jobId } : {}),
    job_ref: jobRef,
    customer_ref: d.customerRef || (d.releaseTo.name ? `${jobRef}-${d.releaseTo.name}` : jobRef),
    collect_from_code: d.collectFrom.name || null,
    collect_from_addr: d.collectFrom.lines.join('\n') || null,
    collect_from_customer_id: d.collectFrom.customerId,
    collect_from_warehouse_id: d.collectFrom.warehouseId,
    release_to_name: d.releaseTo.name || '',
    release_to_addr: d.releaseTo.lines.join('\n') || null,
    release_to_phone: d.releaseTo.phone || null,
    release_to_customer_id: d.releaseTo.customerId,
    release_to_warehouse_id: d.releaseTo.warehouseId,
    deliver_to_name: isCollectionAndDelivery ? (d.deliverTo.name || null) : null,
    deliver_to_addr: isCollectionAndDelivery ? (d.deliverTo.lines.join('\n') || null) : null,
    deliver_to_phone: isCollectionAndDelivery ? (d.deliverTo.phone || null) : null,
    deliver_to_customer_id: isCollectionAndDelivery ? d.deliverTo.customerId : null,
    // Parsed from the destination address; a hand-corrected value on the note being re-issued is carried over.
    delivery_postcode: deliveryPostcodeForSave({
      note_type: noteType, fulfilment_type: resolved.fulfilment.type,
      deliver_to_addr: isCollectionAndDelivery ? d.deliverTo.lines.join('\n') : null, release_to_addr: d.releaseTo.lines.join('\n'),
    }, a.previousNote),
    vehicle_reg: (noteType === 'COLLECTION_SELF' || noteType === 'COLLECTION_SUPPLIER') ? (d.vehicleReg || null) : null,
    driver_name: (noteType === 'COLLECTION_SELF' || noteType === 'COLLECTION_SUPPLIER') ? (d.driverName || null) : null,
    scheduled_date: d.scheduledDate,
    scheduled_time: d.scheduledTime || null,
    marks_numbers: d.marks.split('\n').map(s => s.trim()).filter(Boolean),
    description: description || null,
    summary_text: summary || null,
    nop, pallets_qty: auto.pallets, weight_kg: weightKg, cube_cbm: cbm, remarks,
    notes: d.notes || null,
    authorise_to: authoriseTo || null,
    issued_by_user_id: a.issuer.userId,
    issued_by_name: a.issuer.name,
    item_ids: auto.itemIds,
    // The row this note was raised from (group's first item); readers match it against every item of the group.
    source_devanning_item_id: auto.itemIds[0] ?? null,
    overridden_fields: overriddenFields,
    fulfilment_type: resolved.fulfilment.type,
    fulfilment_type_overridden: resolved.fulfilment.overridden,
  }
}

// A Collection/Delivery Note raised for one shipment row of a group: the row's data is carried into the draft (quantities as typed values, so
// the editor treats them as editable, not as read-only autos). Nothing here is final: staff change anything (e.g. only half collected, or a
// delivery address that differs from the consignee's). Collect From and Release To stay empty: they are not known from the row.
// With a consignee on the row the note starts as Collection + Delivery to that consignee (still editable); without one the choice is left open.
export interface GroupRowForNote {
  job_ref: string
  awb: string | null
  description: string | null
  cartons: number
  gross_weight: number
  cbm: number
  consignee_id: string | null
  consignee_name: string | null
}

export function draftFromGroupRow(row: GroupRowForNote, consigneeAddressLines: string[]): NoteDraft {
  const d = blankNoteDraft('COLLECTION_COURIER', null, '', null)
  return {
    ...d,
    descriptionOverride: row.description || null,
    nopOverride: row.cartons > 0 ? row.cartons : null,
    weightKgOverride: row.gross_weight > 0 ? row.gross_weight : null,
    cbmOverride: row.cbm > 0 ? row.cbm : null,
    remarksOverride: row.awb ? `AWB: ${row.awb}` : null,
    deliverTo: row.consignee_id
      ? { name: row.consignee_name || '', lines: consigneeAddressLines, phone: null, customerId: row.consignee_id, warehouseId: null }
      : emptyAddress(),
    fulfilmentType: row.consignee_id ? 'COLLECTION_AND_DELIVERY' : null,
  }
}
